import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { queryCache } from 'react-query';

import useResourceQuery from '../hooks/useResourceQuery';
import useResourceMutation from '../hooks/useResourceMutation';

/* eslint-disable react-hooks/rules-of-hooks */
function normalizeResource(resourceDefinition) {
  const {
    actions = {},
    auto = true,
    defaultValue = {},
    params,
    queryKeyPrefix,
    url
  } = resourceDefinition;

  const {
    data,
    isError,
    isFetching,
    isSuccess,
    queryKey,
    refetch,
    updatedAt
  } = useResourceQuery({
    url,
    params,
    prefix: queryKeyPrefix,
    config: {
      enabled: auto
    }
  });

  const normalizedActions = Object.entries(actions).reduce((accActions, [actionName, actionFn]) => {
    function actionDefinition(...args) {
      if (typeof actionFn === 'function') {
        return actionFn(...args);
      }

      return actionFn;
    }
    // HACK: get tpt-connect options from resource definition first
    let refetchAfter;
    let updateStrategy;
    // ANOTHER HACK: try-catch in case the action tries to access an undefined variable
    try {
      ({ refetchAfter, updateStrategy } = actionDefinition({}));
    } catch (e) {
      refetchAfter = false;
      updateStrategy = false;
    }
    const [mutate] = useResourceMutation({
      queryKey,
      refetchAfter,
      updateStrategy,
      config: {
        throwOnError: true // propagate error
      }
    });

    return {
      ...accActions,
      [actionName]: (...args) => mutate({ url, ...actionDefinition(...args) })
    };
  }, {});

  // don't use config.initialData for this type of logic
  // (it'll set status: 'success' and not fetch initially)
  const value = data ? data.data : defaultValue;

  return {
    value,
    meta: {
      isError,
      isFetching,
      isSuccess,
      lastUpdated: updatedAt
    },
    fetch: refetch,
    invalidate: () => queryCache.invalidateQueries(queryKey, { refetchActive: false }),
    ...normalizedActions,

    // override withInvalidateAll logic
    invalidateAll: () => {
      // remove queries entirely to simplify withInfiniteScroll interaction
      queryCache.removeQueries(queryKeyPrefix);
      return Promise.resolve(); // keep it thenable
    }
  };
}

export default function __DEPRECATED_defineResources(mapStateToResources, { pure = true } = {}) {
  return (WrappedComponent) => {
    function TptConnectAdapter({ resourceDefinitions, ...rest }) {
      // TODO: memoize
      const resources = Object.entries(resourceDefinitions).reduce((accResources, [key, def]) => ({
        ...accResources,
        [key]: normalizeResource(def)
      }), {});

      return (
        <WrappedComponent
          { ...rest }
          { ...resources }
        />
      );
    }

    TptConnectAdapter.propTypes = {
      resourceDefinitions: PropTypes.object.isRequired
    };

    return connect((state, ownProps) => ({
      resourceDefinitions: mapStateToResources(state, ownProps)
    }), null, null, { pure })(TptConnectAdapter);
  };
}
/* eslint-enable react-hooks/rules-of-hooks */
